import React, { useEffect, useState } from "react";
import { MdEdit } from "react-icons/md";
import { MdDelete } from "react-icons/md";
import { useInteriorDesignCategory } from "../useHook";
import Swal from "sweetalert2";
import InteriorDesignCategoryModal from "./component/InteriorDesignCategoryModal";
import RecordPosition from "../../../Components/Modals/RecordPosition/RecordPosition";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import { ImageWithFallback } from "../../../Utils/loader";
import Loader from "../../../Utils/loader";
import {
  FiEdit2,
  FiFilter,
  FiPlus,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";

const InteriorDesignCategory = () => {
  const { hasPermission } = usePermission();
  const canRead = hasPermission(SystemPermissions.InteriorDesignCategories.READ);
  const canCreate = hasPermission(SystemPermissions.InteriorDesignCategories.CREATE);
  const canUpdate = hasPermission(SystemPermissions.InteriorDesignCategories.UPDATE);
  const canDelete = hasPermission(SystemPermissions.InteriorDesignCategories.DELETE);
  const canToggle = hasPermission(SystemPermissions.InteriorDesignCategories.TOGGLE);
  const canPosition = hasPermission(SystemPermissions.InteriorDesignCategories.POSITION);
  const canShowActions = canUpdate || canDelete;

  const {
    addInteriorDesignCategory,
    getInteriorDesignCategoriesWithPagination,
    updateInteriorDesignCategory,
    deleteInteriorDesignCategory,
    toggleInteriorDesignCategoryStatus,
    updateInteriorDesignCategoryPosition,
  } = useInteriorDesignCategory();

  const [categories, setCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Search state
  const [searchName, setSearchName] = useState("");
  const [searchStatus, setSearchStatus] = useState("");

  // Position modal state
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedCategoryForPosition, setSelectedCategoryForPosition] =
    useState(null);

  useEffect(() => {
    fetchCategories();
  }, [currentPage]);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const offset = (currentPage - 1) * limit;
      const response = await getInteriorDesignCategoriesWithPagination(
        offset,
        limit,
        searchName,
        searchStatus,
      );
      if (response && response.data) {
        setCategories(response.data);
        if (response.pagination) {
          setTotal(response.pagination.totalItems);
          setTotalPages(response.pagination.totalPages);
        }
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      Swal.fire("Error", "Failed to fetch categories", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newOffset) => {
    const newPage = Math.floor(newOffset / limit) + 1;
    setCurrentPage(newPage);
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when searching
    fetchCategories();
  };

  const handleClearSearch = async () => {
    setSearchName("");
    setSearchStatus("");
    setCurrentPage(1);
    // Fetch with cleared values immediately
    setIsLoading(true);
    try {
      const offset = 0;
      const response = await getInteriorDesignCategoriesWithPagination(
        offset,
        limit,
        "",
        "",
      );
      if (response && response.data) {
        setCategories(response.data);
        if (response.pagination) {
          setTotal(response.pagination.totalItems);
          setTotalPages(response.pagination.totalPages);
        }
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      Swal.fire("Error", "Failed to fetch categories", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const handleSaveCategory = async (categoryData, id = null) => {
    setIsLoading(true);
    try {
      let response;
      if (id) {
        response = await updateInteriorDesignCategory(id, categoryData);
      } else {
        response = await addInteriorDesignCategory(categoryData);
      }

      if (response) {
        await fetchCategories();
        toggleModal();
        Swal.fire(
          "Success",
          `Category ${id ? "updated" : "added"} successfully!`,
          "success",
        );
      }
    } catch (error) {
      console.error("Error in handleSaveCategory:", error);
      Swal.fire("Error", error.message || "Failed to save category.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setCurrentCategory(category);
    toggleModal();
  };

  const handleDeleteCategory = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        const response = await deleteInteriorDesignCategory(id);
        if (response) {
          await fetchCategories();
          Swal.fire("Deleted!", "Category has been deleted.", "success");
        }
      } catch (error) {
        Swal.fire(
          "Error",
          error.message || "Failed to delete category.",
          "error",
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const response = await toggleInteriorDesignCategoryStatus(id, newStatus);

      if (response) {
        setCategories((prevCategories) =>
          prevCategories.map((category) =>
            category.id === id ? { ...category, status: newStatus } : category,
          ),
        );

        Swal.fire({
          title: "Success",
          text: `Category status has been ${newStatus ? "activated" : "deactivated"} successfully!`,
          icon: "success",
          timer: 1000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to update category status.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const handlePositionClick = (category) => {
    setSelectedCategoryForPosition(category);
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (categoryId, newPosition) => {
    await updateInteriorDesignCategoryPosition(categoryId, newPosition);
    await fetchCategories();
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedCategoryForPosition(null);
  };

  if (isLoading) {
    return <Loader message="Loading interior design categories..." />;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Interior Design Categories</h2>

        {canCreate && (
          <button
            onClick={() => {
              setCurrentCategory(null);
              toggleModal();
            }}
            className="btn btn-primary h-[44px]"
          >
            <FiPlus className="text-white" />
            Add Interior Design Category
          </button>
        )}
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by category name"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <select
          className="status-select"
          value={searchStatus}
          onChange={(e) => setSearchStatus(e.target.value)}
        >
          <option value="" className="opacity-25">
            Select Status
          </option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>

        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleClearSearch}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Image</th>
                <th className="table-th">Category Name</th>
                <th className="table-th">Description</th>
                <th className="table-th">Status</th>
                {canShowActions && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {Array.isArray(categories) && categories.length > 0 ? (
                categories.map((category, index) => (
                  <tr
                    key={category.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">
                      {canPosition ? (
                        <button
                          type="button"
                          onClick={() => handlePositionClick(category)}
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                        >
                          {category.position ??
                            (currentPage - 1) * limit + index + 1}
                        </button>
                      ) : (
                        <span className="font-medium text-gray-800">
                          {category.position ??
                            (currentPage - 1) * limit + index + 1}
                        </span>
                      )}
                    </td>
                    <td className="table-td">
                      {category.image_url ? (
                        <ImageWithFallback
                          src={category.image_url}
                          alt={category.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-200 rounded-md flex items-center justify-center">
                          <span className="text-gray-500 text-xs">
                            No Image
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="table-td">{category.name}</td>
                    <td className="table-td max-w-xs truncate">
                      {category.description}
                    </td>
                    <td className="table-td">
                      {canToggle ? (
                        <div
                          className={`status-toggle ${
                            category.status ? "active" : "bg-[#cccccc]"
                          }`}
                          onClick={() =>
                            handleToggleStatus(category.id, category.status)
                          }
                        >
                          <div className="status-toggle-handle"></div>
                        </div>
                      ) : (
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            category.status
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {category.status ? "Active" : "Inactive"}
                        </span>
                      )}
                    </td>
                    {canShowActions && (
                      <td className="table-td flex space-x-2 mt-3">
                        {canUpdate && (
                          <button
                            className="btn btn-edit text-white"
                            onClick={() => handleEditCategory(category)}
                          >
                            <FiEdit2 className="text-white" /> Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="btn btn-delete text-white"
                            onClick={() => handleDeleteCategory(category.id)}
                          >
                            <MdDelete className="text-white" /> Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={canShowActions ? 6 : 5} className="text-center py-6 text-gray-500">
                    No interior design categories found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6">
          <GlobalPagination
            offset={(currentPage - 1) * limit}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      <InteriorDesignCategoryModal
        modalOpen={modalOpen}
        toggleModal={toggleModal}
        category={currentCategory}
        handleSaveCategory={handleSaveCategory}
      />

      {isPositionModalOpen && selectedCategoryForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={
            selectedCategoryForPosition.position ??
            (currentPage - 1) * limit +
              categories.findIndex(
                (c) => c.id === selectedCategoryForPosition.id,
              ) +
              1
          }
          recordId={selectedCategoryForPosition.id}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Interior Design Category"
        />
      )}
    </div>
  );
};

export default InteriorDesignCategory;
